import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import i18n from '../i18n.js';
import { resolveUserCrmMode } from './crm-mode-preference.js';
import { normalizeApiErrorPayload } from './localize-api-error.js';
import { isLoginEntryPathname } from './login-entry.js';

// 默认用相对路径 /v1 — 请求走 Vite proxy (dev) 或同源反向代理 (prod),
// 避免 localhost 硬编码导致局域网访问时指向客户端自身.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/v1';
export const CREST_API_BASE_URL = import.meta.env.VITE_CREST_API_BASE_URL || API_BASE_URL;
export const TOKEN_KEY = 'crest_token';
export const USER_KEY = 'crest_user';
const DEVICE_ID_KEY = 'crest_device_id';

type RecoverableRequestConfig = InternalAxiosRequestConfig & {
  __crestAccessRefreshAttempted?: boolean;
};

export interface AccessDeniedRecoveryContext {
  request: InternalAxiosRequestConfig;
  errorCode?: string;
}

export interface AccessDeniedRecoveryResult {
  retry: boolean;
}

type AccessDeniedRecoveryHandler = (
  context: AccessDeniedRecoveryContext,
) => Promise<AccessDeniedRecoveryResult>;

let accessDeniedRecoveryHandler: AccessDeniedRecoveryHandler | undefined;

/** Portal 注册一次统一授权刷新器；业务包不反向依赖 Portal 实现。 */
export function registerAccessDeniedRecoveryHandler(
  handler: AccessDeniedRecoveryHandler,
): () => void {
  accessDeniedRecoveryHandler = handler;
  return () => {
    if (accessDeniedRecoveryHandler === handler) accessDeniedRecoveryHandler = undefined;
  };
}

/**
 * 登录端平台标识：注入到每个请求的 X-Client-Platform 头，供后端登录日志/审计区分来源。
 * - Capacitor 原生壳（mobile-h5 装进 App）→ 'h5'
 * - mobile-h5 bundle 在浏览器预览 → 'h5'
 * - crm/crest/erp Web → 'pc-web'
 */
function detectClientPlatform(): string {
  try {
    const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (cap?.isNativePlatform?.()) return 'h5';
  } catch {
    // ignore
  }
  if (typeof __CREST_WEB_PROJECT__ !== 'undefined' && __CREST_WEB_PROJECT__ === 'mobile-h5') {
    return 'h5';
  }
  return 'pc-web';
}

/** 稳定设备标识（device-id，替代浏览器拿不到的真实 MAC），本地持久化。 */
function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        globalThis.crypto?.randomUUID?.() ??
        `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

export const http = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
});

export const crestHttp = axios.create({
  baseURL: CREST_API_BASE_URL,
  withCredentials: false,
});

export function applyFreshReadHeaders(
  config: Pick<InternalAxiosRequestConfig, 'method' | 'headers'>,
): void {
  const method = (config.method ?? 'get').toLowerCase();
  if (method !== 'get' && method !== 'head') return;
  config.headers.set('Cache-Control', 'no-cache');
  config.headers.set('Pragma', 'no-cache');
}

function installCrestInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use((config) => {
    applyFreshReadHeaders(config);
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // 客户端平台 + 设备标识（登录日志/审计用）
    config.headers['X-Client-Platform'] = detectClientPlatform();
    config.headers['X-Device-Id'] = getDeviceId();
    if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
      config.headers['Idempotency-Key'] ??= globalThis.crypto?.randomUUID?.() ??
        `idem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }
    config.headers['Accept-Language'] = i18n.language?.toLowerCase().startsWith('en')
      ? 'en'
      : 'zh-CN';
    const rawUser = localStorage.getItem(USER_KEY);
    if (rawUser) {
      try {
        if (!config.headers['x-crest-crm-mode']) {
          config.headers['x-crest-crm-mode'] = resolveUserCrmMode(JSON.parse(rawUser));
        }
      } catch {
        // ignore malformed cached user context
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const request = err?.config as RecoverableRequestConfig | undefined;
      if (
        err?.response?.status === 403 &&
        request &&
        !request.__crestAccessRefreshAttempted &&
        !isRuntimeContextRequest(request.url) &&
        accessDeniedRecoveryHandler
      ) {
        request.__crestAccessRefreshAttempted = true;
        try {
          const result = await accessDeniedRecoveryHandler({
            request,
            errorCode: readErrorCode(err.response.data),
          });
          if (result.retry) return instance.request(request);
        } catch {
          // 权限刷新失败时保留原始 403，避免把诊断原因替换成二次错误。
        }
      }
      if (err?.response && 'data' in err.response) {
        err.response.data = normalizeApiErrorPayload(err.response.data, i18n.language);
      }
      if (
        err?.response?.status === 401 &&
        shouldResetPlatformSession(request?.url, request?.baseURL)
      ) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        const pathname = window.location.pathname;
        if (!isLoginEntryPathname(pathname)) {
          window.location.assign(platformLoginUrl());
        }
      }
      return Promise.reject(err);
    },
  );
}

/**
 * 通知属于业务域的可选能力；它无权判定 Platform 登录态是否失效。
 * CRM 尚未建立用户映射时可能返回 401，此时只让通知组件降级为空态。
 */
export function shouldResetPlatformSession(
  url: string | undefined,
  baseURL?: string,
): boolean {
  if (!url) return true;
  const basePath = new URL(baseURL ?? '/', 'http://crest.local').pathname;
  // 子域服务使用 Platform 签发的令牌，但其本地密钥/用户映射异常时也可能返回 401。
  // 这只能让对应业务能力降级，不能据此删除 Platform 主登录态。
  if (/^\/api\/(?:crm|agent|operations)\/v1(?:\/|$)/.test(basePath)) return false;
  const pathname = new URL(url, 'http://crest.local').pathname.replace(/^\/v1(?=\/)/, '');
  return pathname !== '/notifications' && !pathname.startsWith('/notifications/');
}

function isRuntimeContextRequest(url: string | undefined): boolean {
  return Boolean(url?.includes('/apps/runtime-context'));
}

function readErrorCode(data: unknown): string | undefined {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return undefined;
  const value = (data as { errorCode?: unknown }).errorCode;
  return typeof value === 'string' ? value : undefined;
}

installCrestInterceptors(http);
installCrestInterceptors(crestHttp);

function platformLoginUrl(): string {
  const configured = import.meta.env.VITE_CREST_WEB_BASE_URL;
  if (configured) return new URL('/login', `${configured.replace(/\/+$/, '')}/`).toString();
  if (typeof window === 'undefined') return '/login';
  const { protocol, hostname, port, origin } = window.location;
  if (
    typeof __CREST_WEB_PROJECT__ !== 'undefined' &&
    (__CREST_WEB_PROJECT__ === 'crest-platform' || __CREST_WEB_PROJECT__ === 'crest-web')
  ) {
    return new URL('/login', `${origin}/`).toString();
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') {
    return `${protocol}//${hostname}:5174/login`;
  }
  if (port === '5175' || port === '5176') {
    return `${protocol}//${hostname}:5174/login`;
  }
  return new URL('/login', `${origin}/`).toString();
}
